import {gql} from '@apollo/client';
import {compact} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {OrganizationFilterOrganization as GeneratedOrganizationFilterOrganization} from 'search/filters/__generated/OrganizationFilterOrganization';
import DropdownFilterForm, {getFilterText} from 'search/filters/base/DropdownFilterForm';
import {FilterKey, GenericFilter, SingleValueFilter} from 'search/SearchConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

export type OrganizationFilterOrganization = Omit<GeneratedOrganizationFilterOrganization, '__typename'>;

export const ORG_FILTER_ORG_FRAGMENT = gql`
  fragment OrganizationFilterOrganization on OrganizationNode {
    id
    name
  }
`;

export const OrganizationFilterOrganizationFragment: FragmentWrapper<OrganizationFilterOrganization> = {
  fragmentName: 'OrganizationFilterFormOrganization',
  fragment: ORG_FILTER_ORG_FRAGMENT,
};

interface OrganizationFilterFormProps extends BaseFilterFormProps {
  filter: GenericFilter<FilterKey.ORGANIZATION>;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: GenericFilter<FilterKey.ORGANIZATION>) => void;
  onRemove?: () => void;
  organizations: Array<OrganizationFilterOrganization>;
}

export interface OrganizationFilterFormExtraProps {
  organizations: Array<OrganizationFilterOrganization>;
  shouldIncludeNone?: boolean;
}

export const NONE_FILTER = {
  name: 'No organizations',
  value: 'none',
};

const ALL_FILTER = {
  name: 'Any organization',
  value: null,
};

function getActiveFilterText(optionDatas: Array<OptionData>, filter: SingleValueFilter): string {
  if (filter.value == null) {
    return 'Organization';
  }

  let textToPrefix = getFilterText(optionDatas, filter);

  if (filter.value === NONE_FILTER.value) {
    textToPrefix = 'None';
  }

  return `Organization: ${textToPrefix}`;
}

/**
 * A filter for filtering by organization.
 */
export default function OrganizationFilterForm(
  props: OrganizationFilterFormProps & OrganizationFilterFormExtraProps
): JSX.Element {
  const baseProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const orgs = props.organizations.map((org) => ({
    name: org.name,
    value: org.id,
    icon: 'organization',
  }));
  const optionDatas = compact<OptionData>([
    ALL_FILTER,
    props.shouldIncludeNone ? NONE_FILTER : undefined,
    ..._.sortBy(orgs, (org) => org.name.toLowerCase()),
  ]);

  return (
    <DropdownFilterForm
      {...baseProps}
      dropdownToggleText={getActiveFilterText(optionDatas, props.filter)}
      filterStyle={props.filterStyle}
      isMulti={false}
      isSearchable={true}
      onChangeValue={(value: Array<string>) => {
        // @ts-expect-error BNCH(56464): Fix this. ORGANIZATION is a SingleValueFilter in
        // `SearchConstants.ts`, but here we are trying to assign an array of strings.
        props.onChangeFilter({...props.filter, value});
      }}
      onRemove={props.onRemove}
      optionDatas={optionDatas}
      value={props.filter.value}
    />
  );
}
